/**
 * Range1323Strategy
 * Handles '13 to '23 timeline range (2013-2023)
 * Displays content for Income Statement, Balance Sheet, and Cash Flow tabs when period type is Annual
 */

import type { ContentStrategy, PeriodType, TabName } from '@/types';
import { DOM_SELECTORS, IMAGE_PATHS } from '@/utils/constants';
import {
  showElements,
  hideElements,
  removeSections,
  createImageHTML,
  injectContent,
  replaceTableHTML,
  hideNoDataMessage,
  showKeyStatsContent,
} from './ContentHelper';

/**
 * Strategy for 2013-2023 timeline range
 * Shows special 10-year table for Key Stats, images for Income/Balance/Cash (Annual period only)
 */
export class Range1323Strategy implements ContentStrategy {
  private originalTableHTML: string = '';

  /**
   * Apply strategy for 2013-2023 timeline
   */
  public apply(tab: TabName, periodType: PeriodType): void {
    // Only show content for Annual period type
    if (periodType !== 'Annual') {
      this.hideAllContent();
      return;
    }

    // Apply content based on tab
    switch (tab) {
      case 'key-stats':
        this.applyKeyStatsContent();
        break;
      case 'income-statement':
        this.applyIncomeStatementContent();
        break;
      case 'balance-sheet':
        this.applyBalanceSheetContent();
        break;
      case 'cash-flow':
        this.applyCashFlowContent();
        break;
      default:
        this.hideAllContent();
    }
  }

  /**
   * Apply Key Stats content: replace table with 2013-2023 data (10 years)
   */
  private applyKeyStatsContent(): void {
    // Remove all financial statement sections
    removeSections([
      DOM_SELECTORS.INCOME_SECTION_2020_2023,
      DOM_SELECTORS.INCOME_SECTION_1925,
      DOM_SELECTORS.INCOME_SECTION_1323,
      DOM_SELECTORS.BALANCE_SECTION_2020_2023,
      DOM_SELECTORS.BALANCE_SECTION_1925,
      DOM_SELECTORS.BALANCE_SECTION_1323,
      DOM_SELECTORS.CASH_SECTION_2020_2023,
      DOM_SELECTORS.CASH_SECTION_1925,
      DOM_SELECTORS.CASH_SECTION_1323,
    ]);

    // Hide any "No data available" message that might be visible
    hideNoDataMessage();

    // Show the original Key Stats content from the view
    showKeyStatsContent();

    // Show key financials sections, hide cap/val
    showElements([
      DOM_SELECTORS.KEY_FINANCIALS,
      DOM_SELECTORS.FINANCIALS_TABLE_CONTAINER,
      DOM_SELECTORS.FOOTNOTE,
    ]);

    hideElements([
      DOM_SELECTORS.CAPITALIZATION_SECTION,
      DOM_SELECTORS.VALUATION_SECTION,
    ]);

    // Replace table with 2013-2023 data
    if (!this.originalTableHTML) {
      this.originalTableHTML = replaceTableHTML(
        DOM_SELECTORS.FINANCIALS_TABLE_CONTAINER,
        this.get2013To2023TableHTML()
      );
    }

    // Add 2020-2023 capitalization/valuation sections
    this.add2020To2023CapitalizationSection();
  }

  /**
   * Apply Income Statement content for 2013-2023 Annual
   */
  private applyIncomeStatementContent(): void {
    // Hide standard sections
    hideElements([
      DOM_SELECTORS.KEY_FINANCIALS,
      DOM_SELECTORS.FINANCIALS_TABLE_CONTAINER,
      DOM_SELECTORS.FOOTNOTE,
      DOM_SELECTORS.CAPITALIZATION_SECTION,
      DOM_SELECTORS.VALUATION_SECTION,
    ]);

    // Remove other financial statement sections
    removeSections([
      DOM_SELECTORS.INCOME_SECTION_2020_2023,
      DOM_SELECTORS.INCOME_SECTION_1925,
      DOM_SELECTORS.BALANCE_SECTION_2020_2023,
      DOM_SELECTORS.BALANCE_SECTION_1925,
      DOM_SELECTORS.CASH_SECTION_2020_2023,
      DOM_SELECTORS.CASH_SECTION_1925,
      DOM_SELECTORS.CASH_SECTION_1323,
    ]);

    // Remove existing 13-23 income statement section if present
    removeSections([DOM_SELECTORS.INCOME_SECTION_1323]);

    // Create and inject 2013-2023 income statement image
    const incomeHTML = createImageHTML(
      IMAGE_PATHS.INCOME_1323_ANNUAL,
      'income-statement-section-1323',
      {
        'margin-top': '16px',
        'margin-left': '20px',
        'padding-bottom': '20px',
      }
    );

    const success1 = injectContent(DOM_SELECTORS.MAIN_SECTION, incomeHTML);
    if (!success1) {
      console.error('[Range1323Strategy] Failed to inject income statement content');
    }
  }

  /**
   * Apply Balance Sheet content for 2013-2023 Annual
   */
  private applyBalanceSheetContent(): void {
    // Hide standard sections
    hideElements([
      DOM_SELECTORS.KEY_FINANCIALS,
      DOM_SELECTORS.FINANCIALS_TABLE_CONTAINER,
      DOM_SELECTORS.FOOTNOTE,
      DOM_SELECTORS.CAPITALIZATION_SECTION,
      DOM_SELECTORS.VALUATION_SECTION,
    ]);

    // Remove other financial statement sections
    removeSections([
      DOM_SELECTORS.INCOME_SECTION_2020_2023,
      DOM_SELECTORS.INCOME_SECTION_1925,
      DOM_SELECTORS.BALANCE_SECTION_2020_2023,
      DOM_SELECTORS.BALANCE_SECTION_1925,
      DOM_SELECTORS.CASH_SECTION_2020_2023,
      DOM_SELECTORS.CASH_SECTION_1925,
      DOM_SELECTORS.CASH_SECTION_1323,
    ]);

    // Remove existing 13-23 balance sheet section if present
    removeSections([DOM_SELECTORS.BALANCE_SECTION_1323]);

    // Create and inject 2013-2023 balance sheet image
    const balanceHTML = createImageHTML(
      IMAGE_PATHS.BALANCE_1323_ANNUAL,
      'balance-sheet-section-1323',
      {
        'margin-top': '16px',
        'margin-left': '20px',
        'padding-bottom': '20px',
      }
    );

    const success2 = injectContent(DOM_SELECTORS.MAIN_SECTION, balanceHTML);
    if (!success2) {
      console.error('[Range1323Strategy] Failed to inject balance sheet content');
    }
  }

  /**
   * Apply Cash Flow content for 2013-2023 Annual
   */
  private applyCashFlowContent(): void {
    // Hide standard sections
    hideElements([
      DOM_SELECTORS.KEY_FINANCIALS,
      DOM_SELECTORS.FINANCIALS_TABLE_CONTAINER,
      DOM_SELECTORS.FOOTNOTE,
      DOM_SELECTORS.CAPITALIZATION_SECTION,
      DOM_SELECTORS.VALUATION_SECTION,
    ]);

    // Remove other financial statement sections
    removeSections([
      DOM_SELECTORS.INCOME_SECTION_2020_2023,
      DOM_SELECTORS.INCOME_SECTION_1925,
      DOM_SELECTORS.BALANCE_SECTION_2020_2023,
      DOM_SELECTORS.BALANCE_SECTION_1925,
      DOM_SELECTORS.CASH_SECTION_2020_2023,
      DOM_SELECTORS.CASH_SECTION_1925,
    ]);

    // Remove existing 13-23 cash flow section if present
    removeSections([DOM_SELECTORS.CASH_SECTION_1323]);

    // Create and inject 2013-2023 cash flow image
    const cashFlowHTML = createImageHTML(
      IMAGE_PATHS.CASH_1323_ANNUAL,
      'cash-flow-section-1323',
      {
        'margin-top': '16px',
        'margin-left': '20px',
        'padding-bottom': '20px',
      }
    );

    const success3 = injectContent(DOM_SELECTORS.MAIN_SECTION, cashFlowHTML);
    if (!success3) {
      console.error('[Range1323Strategy] Failed to inject cash flow content');
    }
  }

  /**
   * Get the 2013-2023 table HTML (10 years of data: 2014-2023)
   */
  private get2013To2023TableHTML(): string {
    return `
    <table class="financials-table" style="width:100%; border: 1px solid #ccc;">
      <thead>
        <tr>
          <th scope="col" class="label-column">For the Fiscal Period Ending</th>
          <th scope="col">12 months<br>Jan-26-<br>2014A</th>
          <th scope="col">12 months<br>Jan-25-<br>2015A</th>
          <th scope="col">12 months<br>Jan-31-<br>2016A</th>
          <th scope="col">12 months<br>Jan-29-<br>2017A</th>
          <th scope="col">12 months<br>Jan-28-<br>2018A</th>
          <th scope="col">12 months<br>Jan-27-<br>2019A</th>
          <th scope="col">12 months<br>Jan-26-<br>2020A</th>
          <th scope="col">12 months<br>Jan-31-<br>2021A</th>
          <th scope="col">12 months<br>Jan-30-<br>2022A</th>
          <th scope="col">12 months<br>Jan-29-<br>2023A</th>
        </tr>
        <tr>
          <td class="label-column"><em>Currency</em></td>
          <td style="text-align:center; font-style:italic; color:#444;">USD</td>
          <td style="text-align:center; font-style:italic; color:#444;">USD</td>
          <td style="text-align:center; font-style:italic; color:#444;">USD</td>
          <td style="text-align:center; font-style:italic; color:#444;">USD</td>
          <td style="text-align:center; font-style:italic; color:#444;">USD</td>
          <td style="text-align:center; font-style:italic; color:#444;">USD</td>
          <td style="text-align:center; font-style:italic; color:#444;">USD</td>
          <td style="text-align:center; font-style:italic; color:#444;">USD</td>
          <td style="text-align:center; font-style:italic; color:#444;">USD</td>
          <td style="text-align:center; font-style:italic; color:#444;">USD</td>
        </tr>
      </thead>
      <tbody>
        <tr>
          <td class="label-column">Total Revenue<br><span style="margin-left: 1.5em; color: #555; font-size: 0.95em; font-style: italic;">Growth Over Prior Year</span></td>
          <td class="number-cell">4,130.0<br><span style="margin-left: 1.5em; color: #555; font-style: italic;">(3.5%)</span></td>
          <td class="number-cell">4,682.0<br><span style="margin-left: 1.5em; color: #555; font-style: italic;">13.4%</span></td>
          <td class="number-cell">5,010.0<br><span style="margin-left: 1.5em; color: #555; font-style: italic;">7.0%</span></td>
          <td class="number-cell">6,910.0<br><span style="margin-left: 1.5em; color: #555; font-style: italic;">37.9%</span></td>
          <td class="number-cell">9,714.0<br><span style="margin-left: 1.5em; color: #555; font-style: italic;">40.6%</span></td>
          <td class="number-cell">11,716.0<br><span style="margin-left: 1.5em; color: #555; font-style: italic;">20.6%</span></td>
          <td class="number-cell">10,918.0<br><span style="margin-left: 1.5em; color: #555; font-style: italic;">(6.8%)</span></td>
          <td class="number-cell">16,675.0<br><span style="margin-left: 1.5em; color: #555; font-style: italic;">52.7%</span></td>
          <td class="number-cell">26,914.0<br><span style="margin-left: 1.5em; color: #555; font-style: italic;">61.4%</span></td>
          <td class="number-cell">26,974.0<br><span style="margin-left: 1.5em; color: #555; font-style: italic;">0.2%</span></td>
        </tr>
        <tr>
          <td class="label-column">Gross Profit<br><span style="margin-left: 1.5em; color: #555; font-size: 0.95em; font-style: italic;">Margin %</span></td>
          <td class="number-cell">2,268.0<br><span style="margin-left: 1.5em; color: #555; font-style: italic;">54.9%</span></td>
          <td class="number-cell">2,599.0<br><span style="margin-left: 1.5em; color: #555; font-style: italic;">55.5%</span></td>
          <td class="number-cell">2,811.0<br><span style="margin-left: 1.5em; color: #555; font-style: italic;">56.1%</span></td>
          <td class="number-cell">4,063.0<br><span style="margin-left: 1.5em; color: #555; font-style: italic;">58.8%</span></td>
          <td class="number-cell">5,822.0<br><span style="margin-left: 1.5em; color: #555; font-style: italic;">59.9%</span></td>
          <td class="number-cell">7,171.0<br><span style="margin-left: 1.5em; color: #555; font-style: italic;">61.2%</span></td>
          <td class="number-cell">6,768.0<br><span style="margin-left: 1.5em; color: #555; font-style: italic;">62.0%</span></td>
          <td class="number-cell">10,557.0<br><span style="margin-left: 1.5em; color: #555; font-style: italic;">63.3%</span></td>
          <td class="number-cell">17,475.0<br><span style="margin-left: 1.5em; color: #555; font-style: italic;">64.9%</span></td>
          <td class="number-cell">15,356.0<br><span style="margin-left: 1.5em; color: #555; font-style: italic;">56.9%</span></td>
        </tr>
        <tr>
          <td class="label-column">EBITDA<br><span style="margin-left: 1.5em; color: #555; font-size: 0.95em; font-style: italic;">Margin %</span></td>
          <td class="number-cell">735.0<br><span style="margin-left: 1.5em; color: #555; font-style: italic;">17.8%</span></td>
          <td class="number-cell">979.0<br><span style="margin-left: 1.5em; color: #555; font-style: italic;">20.9%</span></td>
          <td class="number-cell">1,075.0<br><span style="margin-left: 1.5em; color: #555; font-style: italic;">21.5%</span></td>
          <td class="number-cell">2,124.0<br><span style="margin-left: 1.5em; color: #555; font-style: italic;">30.7%</span></td>
          <td class="number-cell">3,409.0<br><span style="margin-left: 1.5em; color: #555; font-style: italic;">35.1%</span></td>
          <td class="number-cell">4,066.0<br><span style="margin-left: 1.5em; color: #555; font-style: italic;">34.7%</span></td>
          <td class="number-cell">3,227.0<br><span style="margin-left: 1.5em; color: #555; font-style: italic;">29.6%</span></td>
          <td class="number-cell">5,819.0<br><span style="margin-left: 1.5em; color: #555; font-style: italic;">34.9%</span></td>
          <td class="number-cell">11,215.0<br><span style="margin-left: 1.5em; color: #555; font-style: italic;">41.7%</span></td>
          <td class="number-cell">7,121.0<br><span style="margin-left: 1.5em; color: #555; font-style: italic;">26.4%</span></td>
        </tr>
        <tr>
          <td class="label-column">EBIT<br><span style="margin-left: 1.5em; color: #555; font-size: 0.95em; font-style: italic;">Margin %</span></td>
          <td class="number-cell">496.0<br><span style="margin-left: 1.5em; color: #555; font-style: italic;">12.0%</span></td>
          <td class="number-cell">759.0<br><span style="margin-left: 1.5em; color: #555; font-style: italic;">16.2%</span></td>
          <td class="number-cell">878.0<br><span style="margin-left: 1.5em; color: #555; font-style: italic;">17.5%</span></td>
          <td class="number-cell">1,937.0<br><span style="margin-left: 1.5em; color: #555; font-style: italic;">28.0%</span></td>
          <td class="number-cell">3,210.0<br><span style="margin-left: 1.5em; color: #555; font-style: italic;">33.0%</span></td>
          <td class="number-cell">3,804.0<br><span style="margin-left: 1.5em; color: #555; font-style: italic;">32.5%</span></td>
          <td class="number-cell">2,846.0<br><span style="margin-left: 1.5em; color: #555; font-style: italic;">26.1%</span></td>
          <td class="number-cell">4,721.0<br><span style="margin-left: 1.5em; color: #555; font-style: italic;">28.3%</span></td>
          <td class="number-cell">10,041.0<br><span style="margin-left: 1.5em; color: #555; font-style: italic;">37.3%</span></td>
          <td class="number-cell">5,577.0<br><span style="margin-left: 1.5em; color: #555; font-style: italic;">20.7%</span></td>
        </tr>
        <tr>
          <td class="label-column">Earnings from Cont. Ops.<br><span style="margin-left: 1.5em; color: #555; font-size: 0.95em; font-style: italic;">Margin %</span></td>
          <td class="number-cell">440.0<br><span style="margin-left: 1.5em; color: #555; font-style: italic;">10.7%</span></td>
          <td class="number-cell">631.0<br><span style="margin-left: 1.5em; color: #555; font-style: italic;">13.5%</span></td>
          <td class="number-cell">614.0<br><span style="margin-left: 1.5em; color: #555; font-style: italic;">12.3%</span></td>
          <td class="number-cell">1,666.0<br><span style="margin-left: 1.5em; color: #555; font-style: italic;">24.1%</span></td>
          <td class="number-cell">3,047.0<br><span style="margin-left: 1.5em; color: #555; font-style: italic;">31.4%</span></td>
          <td class="number-cell">4,141.0<br><span style="margin-left: 1.5em; color: #555; font-style: italic;">35.3%</span></td>
          <td class="number-cell">2,796.0<br><span style="margin-left: 1.5em; color: #555; font-style: italic;">25.6%</span></td>
          <td class="number-cell">4,332.0<br><span style="margin-left: 1.5em; color: #555; font-style: italic;">26.0%</span></td>
          <td class="number-cell">9,752.0<br><span style="margin-left: 1.5em; color: #555; font-style: italic;">36.2%</span></td>
          <td class="number-cell">4,368.0<br><span style="margin-left: 1.5em; color: #555; font-style: italic;">16.2%</span></td>
        </tr>
        <tr>
          <td class="label-column">Net Income<br><span style="margin-left: 1.5em; color: #555; font-size: 0.95em; font-style: italic;">Margin %</span></td>
          <td class="number-cell">631.0<br><span style="margin-left: 1.5em; color: #555; font-style: italic;">15.3%</span></td>
          <td class="number-cell">630.0<br><span style="margin-left: 1.5em; color: #555; font-style: italic;">13.5%</span></td>
          <td class="number-cell">631.0<br><span style="margin-left: 1.5em; color: #555; font-style: italic;">12.6%</span></td>
          <td class="number-cell">1,186.0<br><span style="margin-left: 1.5em; color: #555; font-style: italic;">17.2%</span></td>
          <td class="number-cell">2,463.0<br><span style="margin-left: 1.5em; color: #555; font-style: italic;">25.4%</span></td>
          <td class="number-cell">3,047.0<br><span style="margin-left: 1.5em; color: #555; font-style: italic;">26.0%</span></td>
          <td class="number-cell">2,161.0<br><span style="margin-left: 1.5em; color: #555; font-style: italic;">19.8%</span></td>
          <td class="number-cell">4,332.0<br><span style="margin-left: 1.5em; color: #555; font-style: italic;">26.0%</span></td>
          <td class="number-cell">9,752.0<br><span style="margin-left: 1.5em; color: #555; font-style: italic;">36.2%</span></td>
          <td class="number-cell">4,368.0<br><span style="margin-left: 1.5em; color: #555; font-style: italic;">16.2%</span></td>
        </tr>
        <tr>
          <td class="label-column">Diluted EPS Excl. Extra Items<sup>3</sup><br><span style="margin-left: 1.5em; color: #555; font-size: 0.95em; font-style: italic;">Growth Over Prior Year</span></td>
          <td class="number-cell">0.02<br><span style="margin-left: 1.5em; color: #555; font-style: italic;">(17.8%)</span></td>
          <td class="number-cell">0.03<br><span style="margin-left: 1.5em; color: #555; font-style: italic;">51.4%</span></td>
          <td class="number-cell">0.03<br><span style="margin-left: 1.5em; color: #555; font-style: italic;">(3.6%)</span></td>
          <td class="number-cell">0.06<br><span style="margin-left: 1.5em; color: #555; font-style: italic;">138.1%</span></td>
          <td class="number-cell">0.12<br><span style="margin-left: 1.5em; color: #555; font-style: italic;">87.4%</span></td>
          <td class="number-cell">0.17<br><span style="margin-left: 1.5em; color: #555; font-style: italic;">37.6%</span></td>
          <td class="number-cell">0.11<br><span style="margin-left: 1.5em; color: #555; font-style: italic;">(31.8%)</span></td>
          <td class="number-cell">0.17<br><span style="margin-left: 1.5em; color: #555; font-style: italic;">53.1%</span></td>
          <td class="number-cell">0.39<br><span style="margin-left: 1.5em; color: #555; font-style: italic;">122.5%</span></td>
          <td class="number-cell">0.17<br><span style="margin-left: 1.5em; color: #555; font-style: italic;">(55.8%)</span></td>
        </tr>
      </tbody>
    </table>
    `;
  }

  /**
   * Hide all content for non-Annual periods and show "No data available" message
   */
  private hideAllContent(): void {
    // Hide all main sections
    hideElements([
      DOM_SELECTORS.KEY_FINANCIALS,
      DOM_SELECTORS.FINANCIALS_TABLE_CONTAINER,
      DOM_SELECTORS.FOOTNOTE,
      DOM_SELECTORS.CAPITALIZATION_SECTION,
      DOM_SELECTORS.VALUATION_SECTION,
    ]);

    // Remove all financial statement sections
    removeSections([
      DOM_SELECTORS.CAP_SECTION_2020_2023,
      DOM_SELECTORS.VAL_SECTION_2020_2023,
      DOM_SELECTORS.INCOME_SECTION_2020_2023,
      DOM_SELECTORS.INCOME_SECTION_1925,
      DOM_SELECTORS.INCOME_SECTION_1323,
      DOM_SELECTORS.BALANCE_SECTION_2020_2023,
      DOM_SELECTORS.BALANCE_SECTION_1925,
      DOM_SELECTORS.BALANCE_SECTION_1323,
      DOM_SELECTORS.CASH_SECTION_2020_2023,
      DOM_SELECTORS.CASH_SECTION_1925,
      DOM_SELECTORS.CASH_SECTION_1323,
    ]);

    // Show "No data available" message
    const mainSection = document.querySelector(DOM_SELECTORS.MAIN_SECTION);
    if (mainSection) {
      mainSection.innerHTML = `
        <div style="display: flex; align-items: center; justify-content: center; min-height: 400px;">
          <p style="font-size: 16px; color: #666; text-align: center; font-family: 'Times New Roman', Times, serif;">
            No data available for this timeline range and period type.
          </p>
        </div>
      `;
    }
  }

  /**
   * Add 2020-2023 capitalization section after footnote
   */
  private add2020To2023CapitalizationSection(): void {
    const mainContent = document.querySelector(DOM_SELECTORS.MAIN_CONTENT);
    if (!mainContent) return;

    // Remove existing sections if present
    removeSections([
      DOM_SELECTORS.CAP_SECTION_2020_2023,
      DOM_SELECTORS.VAL_SECTION_2020_2023,
    ]);

    // Create capitalization and valuation HTML
    const capitalizationHTML = this.get2020To2023CapitalizationHTML();
    const valuationHTML = this.get2020To2023ValuationHTML();

    // Find footnote and insert after it
    const footnote = document.querySelector(DOM_SELECTORS.FOOTNOTE);
    if (footnote) {
      footnote.insertAdjacentHTML('afterend', capitalizationHTML + valuationHTML);
    }
  }

  /**
   * Get 2020-2023 capitalization HTML
   */
  private get2020To2023CapitalizationHTML(): string {
    return `
    <div class="capitalization-section capitalization-section-2020-2023" style="margin-top: 20px;">
      <div class="capitalization-header">
        Current Capitalization (Millions of USD)<a href="#" class="blue-link historical-link">View Historical Capitalization</a>
      </div>
      <div class="cap-container">
        <div class="cap-table-col">
          <table style="width:100%; border-collapse:collapse; font-family:Arial, sans-serif; font-size:12px; color:#333;">
            <tr style="font-style:italic; background:#f0f0f0;">
              <td style="padding:2px 8px; font-style:italic; color:#444; font-weight:bold;">Currency</td>
              <td style="text-align:right; font-style:italic; color:#444; padding:2px 8px; font-weight:bold;">USD</td>
            </tr>
            <tr>
              <td style="padding:2px 8px; color:#444;">Share Price</td>
              <td style="text-align:right; color:#333; padding:2px 8px;">$ 142.50</td>
            </tr>
            <tr style="background:#f8f8f8;">
              <td style="padding:2px 8px; color:#444;">Shares Out.</td>
              <td style="text-align:right; color:#333; padding:2px 8px;">24,650.0</td>
            </tr>
            <tr>
              <td style="font-weight:bold; color:#333; padding:2px 8px;">Market Capitalization</td>
              <td style="text-align:right; font-weight:bold; color:#0056d6; padding:2px 8px;">3,512,625.0</td>
            </tr>
            <tr style="background:#f8f8f8;">
              <td style="padding:2px 8px; color:#444;">- Cash &amp; Short Term Investments</td>
              <td style="text-align:right; color:#333; padding:2px 8px;">21,208.0</td>
            </tr>
            <tr>
              <td style="padding:2px 8px; color:#444;">+ Total Debt</td>
              <td style="text-align:right; color:#333; padding:2px 8px;">8,756.0</td>
            </tr>
            <tr style="background:#f8f8f8;">
              <td style="padding:2px 8px; color:#444;">+ Pref. Equity</td>
              <td style="text-align:right; color:#333; padding:2px 8px;">-</td>
            </tr>
            <tr>
              <td style="padding:2px 8px; color:#444;">+ Total Minority Interest</td>
              <td style="text-align:right; color:#333; padding:2px 8px;">-</td>
            </tr>
            <tr style="background:#f8f8f8;">
              <td style="padding:2px 8px; color:#444;">- Long Term Marketable Securities</td>
              <td style="text-align:right; color:#333; padding:2px 8px;">-</td>
            </tr>
            <tr style="border-top:1px solid #333;">
              <td style="font-weight:bold; color:#333; padding:2px 8px;">= Total Enterprise Value (TEV)</td>
              <td style="text-align:right; font-weight:bold; color:#0056d6; padding:2px 8px;">3,500,173.0</td>
            </tr>
            <tr style="background:#f8f8f8;">
              <td style="padding:2px 8px; color:#444;">Book Value of Common Equity</td>
              <td style="text-align:right; color:#333; padding:2px 8px;">44,537.0</td>
            </tr>
            <tr>
              <td style="padding:2px 8px; color:#444;">+ Pref. Equity</td>
              <td style="text-align:right; color:#333; padding:2px 8px;">-</td>
            </tr>
            <tr style="background:#f8f8f8;">
              <td style="padding:2px 8px; color:#444;">+ Total Minority Interest</td>
              <td style="text-align:right; color:#333; padding:2px 8px;">-</td>
            </tr>
            <tr>
              <td style="padding:2px 8px; color:#444;">+ Total Debt</td>
              <td style="text-align:right; color:#333; padding:2px 8px;">8,756.0</td>
            </tr>
            <tr style="border-top:1px solid #333;">
              <td style="font-weight:bold; color:#333; padding:2px 8px;">= Total Capital</td>
              <td style="text-align:right; font-weight:bold; color:#0056d6; padding:2px 8px;">53,293.0</td>
            </tr>
          </table>
          <div style="margin-top:8px; font-size:11px; color:#555; line-height:1.3;">
            <div>**For companies that have multiple share classes that publicly trade, we are incorporating the different prices to calculate our company level market capitalization. Please click on the value to see the detailed calculation. Prices shown on this page are the close price of the company's primary stock class. Shares shown on this page are total company as-reported share values.</div>
          </div>
        </div>
        <div class="cap-chart-col">
          <img src="/img/cap-chart.png" alt="Capitalization Chart" onerror="this.src='/img/placeholder.png'" style="margin-top: 0px;">
          <div class="chart-legend">
            <div class="legend-items">
              <span class="legend-item"><span class="color-box net-liability"></span>Net Liability</span>
              <span class="legend-item"><span class="color-box total-liability"></span>Total Liability</span>
              <span class="legend-item"><span class="color-box common-equity"></span>Common Equity</span>
              <span class="legend-item"><span class="color-box market-cap"></span>Market Capitalization</span>
            </div>
            <div style="font-size:12px; color:#444; margin-top:4px;">Note: Striped area represents the impact of negative Net Liability on Market Cap.<br>
              Total Liability includes Total Debt, Minority Interest and Pref. Equity.<br>
              Net Liability includes Total Liability, net of Cash and Short Term Investments.<br>
              TEV includes Market Cap and Net Liability.<br>
              Total Capital includes Common Equity and Total Liability.
            </div>
          </div>
        </div>
      </div>
    </div>
  `;
  }

  /**
   * Get 2020-2023 valuation HTML
   */
  private get2020To2023ValuationHTML(): string {
    return `
    <div class="valuation-section valuation-section-2020-2023" style="margin-top:20px; margin-bottom:32px;">
      <div class="capitalization-header" style="width:99%;">
        Valuation Multiples based on Current Capitalization
      </div>
      <div style="width:100%; margin-top:5px;">
        <table style="width:100%; border-collapse:collapse; font-family:Arial, sans-serif; font-size:12px; background:#fafbfc; color:#333;">
          <tr style="background:#e6e6e6;">
            <td style="padding:6px 8px; font-weight:bold; color:#444; font-size:12px;">For the Fiscal Period Ending</td>
            <td style="text-align:center; padding:6px 8px; font-size:12px; font-weight:bold; color:#444;">12 months<br>Jan-26-2025A</td>
            <td style="text-align:center; padding:6px 8px; font-size:12px; font-weight:bold; color:#444;">LTM<br>12 months<br>Apr-27-2025A</td>
            <td style="text-align:center; padding:6px 8px; font-size:12px; font-weight:bold; color:#444;">12 months<br>Jan-31-2026E</td>
            <td style="text-align:center; padding:6px 8px; font-size:12px; font-weight:bold; color:#444;">12 months<br>Jan-31-2027E</td>
            <td style="text-align:center; padding:6px 8px; font-size:12px; font-weight:bold; color:#444;">12 months<br>Jan-31-2028E</td>
          </tr>
          <tr>
            <td style="font-weight:bold; color:#333;">TEV/Total Revenue</td>
            <td style="color:#0056d6; text-align:center; font-weight:normal;">29.1x</td>
            <td style="color:#0056d6; text-align:center; font-weight:normal;">25.5x</td>
            <td style="color:#0056d6; text-align:center; font-weight:normal;">19.24x</td>
            <td style="color:#0056d6; text-align:center; font-weight:normal;">15.33x</td>
            <td style="color:#0056d6; text-align:center; font-weight:normal;">13.21x</td>
          </tr>
          <tr>
            <td style="font-weight:bold; color:#333;">TEV/EBITDA</td>
            <td style="color:#0056d6; text-align:center; font-weight:normal;">45.5x</td>
            <td style="color:#0056d6; text-align:center; font-weight:normal;">42.8x</td>
            <td style="color:#0056d6; text-align:center; font-weight:normal;">32.52x</td>
            <td style="color:#0056d6; text-align:center; font-weight:normal;">25.72x</td>
            <td style="color:#0056d6; text-align:center; font-weight:normal;">22.27x</td>
          </tr>
          <tr>
            <td style="font-weight:bold; color:#333;">TEV/EBIT</td>
            <td style="color:#0056d6; text-align:center; font-weight:normal;">46.7x</td>
            <td style="color:#0056d6; text-align:center; font-weight:normal;">44.0x</td>
            <td style="color:#0056d6; text-align:center; font-weight:normal;">31.04x</td>
            <td style="color:#0056d6; text-align:center; font-weight:normal;">23.33x</td>
            <td style="color:#0056d6; text-align:center; font-weight:normal;">20.35x</td>
          </tr>
          <tr>
            <td style="font-weight:bold; color:#333;">P/Diluted EPS Before Extra</td>
            <td style="color:#0056d6; text-align:center; font-weight:normal;">53.5x</td>
            <td style="color:#0056d6; text-align:center; font-weight:normal;">50.7x</td>
            <td style="color:#0056d6; text-align:center; font-weight:normal;">37.13x</td>
            <td style="color:#0056d6; text-align:center; font-weight:normal;">27.64x</td>
            <td style="color:#0056d6; text-align:center; font-weight:normal;">23.97x</td>
          </tr>
          <tr>
            <td style="font-weight:bold; color:#333;">P/BV</td>
            <td style="color:#0056d6; text-align:center; font-weight:normal;">48.5x</td>
            <td style="color:#0056d6; text-align:center; font-weight:normal;">45.7x</td>
            <td style="color:#0056d6; text-align:center; font-weight:normal;">26.50x</td>
            <td style="color:#0056d6; text-align:center; font-weight:normal;">16.02x</td>
            <td style="color:#0056d6; text-align:center; font-weight:normal;">10.63x</td>
          </tr>
          <tr>
            <td style="font-weight:bold; color:#333;">Price/Tang BV</td>
            <td style="color:#0056d6; text-align:center; font-weight:normal;">52.5x</td>
            <td style="color:#0056d6; text-align:center; font-weight:normal;">49.4x</td>
            <td style="color:#0056d6; text-align:center; font-weight:normal;">-</td>
            <td style="color:#0056d6; text-align:center; font-weight:normal;">-</td>
            <td style="color:#0056d6; text-align:center; font-weight:normal;">-</td>
          </tr>
        </table>
      </div>
    </div>
  `;
  }
}
